/**
 * useWorkOrderActions Hooks
 *
 * React Query mutations for the bulk work-order action endpoints.
 *
 * The UI (stepper modal) speaks a simpler per-job shape — `{ id, comments }` —
 * because that's all the user actually fills in. These hooks translate that
 * into the backend's richer `{ rejections | requests }` payload by stamping
 * `createdBy` from the current auth session and `createdDate` from the client
 * clock. `externalId` is intentionally omitted: the backend reads it from the
 * job row when validating, so shipping it from the client would be redundant
 * (and the jobs list doesn't carry it anyway).
 *
 * All three hooks invalidate the jobs list so rows whose status changed
 * re-render from the server. We invalidate on every resolved mutation
 * because even partial-success responses mutate some rows.
 */

import { useAuth } from '@/domains/auth';
import { queryKeys } from '@/lib/query-client';
import type { OutboundMoreInfoRequestedBody, OutboundRejectedBody } from '@skewb-works/contracts';
import type { OutboundAllocatedBody } from '@skewb-works/contracts';
import type { OutboundDurationChallengeBody } from '@skewb-works/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AbortWorkOrderBody } from '../api/workOrdersApi';
import {
  abortWorkOrder,
  acceptWorkOrders,
  allocateWorkOrder,
  cancelFromAbort,
  cancelWorkOrder,
  durationChallengeWorkOrder,
  engineerEnRoute,
  engineerOnSite,
  engineeringComplete,
  rejectWorkOrders,
  replanFromAbort,
  replyToMoreInfoWorkOrders,
  requestMoreInfoWorkOrders,
  scheduleWorkOrder,
  unscheduleWorkOrder,
  validationRequested,
} from '../api/workOrdersApi';

/**
 * Per-job input collected by the bulk stepper modal. Kept intentionally
 * minimal — enrichment happens inside each hook just before the HTTP call.
 */
export interface BulkJobInput {
  id: number;
  externalId: string;
  comments: string;
  attachments?: OutboundRejectedBody['rejections'][number]['attachments'];
}

function invalidateJobsList(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
  queryClient.invalidateQueries({ queryKey: queryKeys.jobs.workOrderStatuses() });
}

/**
 * Build a single-note payload entry from the UI's `{id, comments}` pair. The
 * backend accepts an array of notes per job but the stepper captures only one
 * free-text field, so we always wrap it in a length-1 array.
 */
function buildNoteItem(
  input: BulkJobInput,
  createdBy: string,
): OutboundRejectedBody['rejections'][number] {
  return {
    id: input.id,
    externalId: input.externalId,
    notes: [
      {
        note: input.comments,
        createdBy,
        createdDate: new Date().toISOString(),
      },
    ],
    attachments: input.attachments,
  };
}

export function useAcceptWorkOrders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => acceptWorkOrders(ids),
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      resourceName: 'Job',
      action: 'bulk',
      count: (variables: number[]) => variables.length,
      successTitle: 'Jobs Accepted',
    },
  });
}

export function useRejectWorkOrders() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: (items: BulkJobInput[]) => {
      const rejections: OutboundRejectedBody['rejections'] = items.map((item) =>
        buildNoteItem(item, createdBy),
      );
      return rejectWorkOrders(rejections);
    },
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      resourceName: 'Job',
      action: 'bulk',
      count: (variables: BulkJobInput[]) => variables.length,
      successTitle: 'Jobs Rejected',
    },
  });
}

export function useRequestMoreInfoWorkOrders() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: (items: BulkJobInput[]) => {
      const requests: OutboundMoreInfoRequestedBody['requests'] = items.map((item) =>
        buildNoteItem(item, createdBy),
      );
      return requestMoreInfoWorkOrders(requests);
    },
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'More Info Requested',
      successMessage: (variables: BulkJobInput[]) =>
        `Requested more info for ${variables.length} ${variables.length === 1 ? 'job' : 'jobs'}`,
    },
  });
}

export function useReplyMoreInfoWorkOrders() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: (items: BulkJobInput[]) => {
      const requests: OutboundMoreInfoRequestedBody['requests'] = items.map((item) =>
        buildNoteItem(item, createdBy),
      );
      return replyToMoreInfoWorkOrders(requests);
    },
    onSuccess: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Reply Sent',
      successMessage: (variables: BulkJobInput[]) =>
        `Replied to ${variables.length} ${variables.length === 1 ? 'job' : 'jobs'}`,
    },
  });
}

export function useScheduleWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => scheduleWorkOrder(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Scheduled',
      successMessage: 'Job moved to scheduled status',
    },
  });
}

export function useAllocateWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OutboundAllocatedBody) => allocateWorkOrder(data),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Allocated',
      successMessage: 'Job assigned to team successfully',
    },
  });
}

export function useUnscheduleWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => unscheduleWorkOrder(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Unscheduled',
      successMessage: 'Job removed from schedule',
    },
  });
}

export function useAbortWorkOrder() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) => {
      const body: AbortWorkOrderBody = {
        id,
        notes: [
          {
            id: crypto.randomUUID(),
            note,
            createdBy,
            createdDate: new Date().toISOString(),
          },
        ],
      };
      return abortWorkOrder(body);
    },
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Aborted',
      successMessage: 'Job status changed to aborted',
    },
  });
}

export function useEngineerEnRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => engineerEnRoute(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Status Updated',
      successMessage: 'Engineer marked as en route',
    },
  });
}

export function useEngineerOnSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => engineerOnSite(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Status Updated',
      successMessage: 'Engineer marked as on site',
    },
  });
}

export function useEngineeringComplete() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) =>
      engineeringComplete({
        id,
        notes: [{ note, createdBy, createdDate: new Date().toISOString() }],
      }),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Engineering Complete',
      successMessage: 'Job marked as complete',
    },
  });
}

export function useValidationRequested() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: ({
      id,
      note,
      invoiceReference,
      invoiceAmount,
    }: {
      id: number;
      note: string;
      invoiceReference?: string;
      invoiceAmount?: number;
    }) =>
      validationRequested({
        id,
        notes: note.trim()
          ? [{ note: note.trim(), createdBy, createdDate: new Date().toISOString() }]
          : undefined,
        invoice:
          invoiceReference?.trim() || invoiceAmount !== undefined
            ? {
                reference: invoiceReference?.trim() || undefined,
                amount: invoiceAmount,
              }
            : undefined,
      }),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Validation Requested',
      successMessage: 'Job submitted for validation',
    },
  });
}

export function useCancelFromAbort() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelFromAbort(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Cancelled',
      successMessage: 'Job cancelled from aborted status',
    },
  });
}

export function useReplanFromAbort() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => replanFromAbort(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Replanned',
      successMessage: 'Job moved back to planning status',
    },
  });
}

export function useCancelWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelWorkOrder(id),
    onSettled: () => invalidateJobsList(queryClient),
    meta: {
      successTitle: 'Job Cancelled',
      successMessage: 'Job cancelled successfully',
    },
  });
}

export function useDurationChallengeWorkOrder() {
  const queryClient = useQueryClient();
  const { fullName, email } = useAuth();
  const createdBy = fullName || email || 'Unknown user';

  return useMutation({
    mutationFn: ({
      jobId,
      diffHours,
      windowStartDate,
      windowEndDate,
    }: {
      jobId: number;
      diffHours: number;
      windowStartDate?: string;
      windowEndDate?: string;
    }) => {
      const body: OutboundDurationChallengeBody = {
        id: jobId,
        schedule: {
          duration: diffHours,
          durationUnit: 'hours',
          windowStartDate,
          windowEndDate,
        },
        notes: [
          {
            note: 'Requesting duration challenge due to selected duration exceeding estimate',
            createdBy,
            createdDate: new Date().toISOString(),
          },
        ],
      };
      return durationChallengeWorkOrder(body);
    },
    onSuccess: () => {
      invalidateJobsList(queryClient);
      queryClient.invalidateQueries({ queryKey: queryKeys.planner.all });
    },
    meta: {
      successTitle: 'Duration Challenge',
      successMessage: 'Duration challenge submitted successfully',
    },
  });
}
